import { Component, OnInit, ViewChild, EventEmitter, Input, Output } from '@angular/core';
import { Table } from 'primeng/table';
import { MenuItem, LazyLoadEvent } from 'primeng/api';
import { TableMeta } from './models/table-meta.model';
import { ColumnsMeta } from './models/columns-meta.model';
import ResolveFieldData from './helpers/resolve-field-data';
import { Account } from './models/account.model';

class Filter {
  pageNumber = 1;
  pageSize = 10;
  sortingColumn: string;
  sortingOrder = true;
  searchTerm = '';
}

@Component({
  selector: 'app-dop-table',
  templateUrl: './dop-table.component.html',
  styleUrls: ['./dop-table.component.scss']
})
export class DopTableComponent implements OnInit {

  @ViewChild('gridTable', { static: true }) gridTable: Table;

  // Has table meta data including following properties
  // displayGlobalSearch, displayCheckBox, displayContextMenu
  // displayKebabMenu, editMenu, deleteMenu, showAccountSearch, displayCreateButton...
  @Input() tableMeta: TableMeta;

  // Has column meta data including following properties
  // field: string, header: string, sortable: bool, selectable: bool, displayIcon: bool...
  @Input() columnsMeta: ColumnsMeta[];

  // Set value to true if you want to disable the search (used when account search is enabled)
  @Input() disableGrid = false;

  // Set the default sorting column, or by default it will take the first field in the table
  @Input() defaultSortColumn: string;

  // Set the Display Text for the Create Button
  @Input() createButtonText = 'Create';

  // Set the Place Holder for global search filter input
  @Input() placeHolderText = 'Search';

  // Set the data of the grid to be displayed
  get gridData(): any {
    return this.gridDataDto;
  }
  @Input()
  set gridData(val: any) {
    this.gridDataDto = [...val];
    this.flag = true;
    this.selectedTableRows = [];
    this.onCheckBoxChange();
  }

  // Set the total Records Property used to display the no of page numbers
  @Input() totalRecords: any;

  // Set user details if needed (to get default account code)
  @Input() user: any;

  // use getter setter to define the property
  // Set the accounts property which is used to display drop down in account search
  get accounts(): any {
    return this.accountsDto;
  }
  @Input()
  set accounts(val: any) {
    if (val) {
      this.setAccountData(val);
    } else {
      this.isAccountExist = true;
    }
  }

  // use getter setter to define the property
  // Set the StoreData property which is used for opening grid with previous state
  get storeData(): any {
    return this.storeDataDto;
  }
  @Input()
  set storeData(val: any) {
    if (val) {
      this.storeDataDto = val;
    }
  }

  // use this field to handle validation related to the grid when the data is loaded or not
  @Input() isDataLoaded = true;

  // Set Import Icon Tool Tip Text
  @Input() importToolTip = 'Import';

  // Set Export Icon Tool Tip Text
  @Input() exportToolTip = 'Export';

  // Set Export Icon Tool Tip Text
  @Input() disableImportExport = true;

  // Event Triggered when there is change in the table filter
  // returns the filter object contain following properties
  // pageNo, offset, sortingColumn, sortingOrder, search
  @Output() filterChangeEvent = new EventEmitter<Filter>();

  // Event Triggered when there is row selection done, if the cell has selectable property in columnsMeta
  // returns the data(object) present in the row
  @Output() rowSelectedEvent = new EventEmitter<any>();

  // Event Triggered when there is a row deletion from the context menu, kebab menu, checkbox selection and deletion
  // returns the data(object) of the row to be deleted
  @Output() rowDeletionEvent = new EventEmitter<any>();

  // Event Triggered when text is entered in the account search input box
  // returns the search text entered
  @Output() accountSearchEvent = new EventEmitter<any>();

  // Event Triggered when the account is selected from the dropDown
  // returns the selected account
  @Output() accountSelectEvent = new EventEmitter<any>();

  // Event Triggered when the Create button is clicked
  @Output() createButtonEvent = new EventEmitter<any>();

  // Event Triggered to send the Grid State to the Parent
  @Output() gridState = new EventEmitter<any>();

  // Event Triggered to send the Grid State to the Parent
  @Output() defaultValueRowEditEvent = new EventEmitter<any>();

  // Event Triggered when the Upload button is clicked
  @Output() uploadButtonEvent = new EventEmitter<any>();

  // Event Triggered when the Download button is clicked
  @Output() downloadButtonEvent = new EventEmitter<any>();


  private storeDataDto: any;
  private accountsDto: any;
  private gridDataDto: any;
  private INTMAXVALUE = 2147483647;

  filter: Filter;
  selectedTableRows: any[] = [];
  noOfSelectedTableRows: number;
  contextMenuOptions: MenuItem[] = [];
  deleteRowData: any;
  paginationState: any = { first: 0, rows: 10 };
  previousSearch = '';
  accountSearch: any;
  dropDownAccountSelected: any;
  accountCode: string;
  isAccountExist = true;
  searchText = '';
  sortOrder = 1;
  booleanTypeValues = [{ label: 'True', value: 'True' }, { label: 'False', value: 'False' }];
  flag = false;
  math = Math;
  sizeOptions: any[] = [10, 20, 30];

  constructor() {
    this.accountCode = '';
  }

  ngOnInit() {
    if (this.disableGrid) {
      this.gridData = [];
    }
    this.filter = new Filter();
    if (this.tableMeta.displayContextMenu) {
      this.setUpContextMenu();
    }
    if (!this.defaultSortColumn) {
      this.defaultSortColumn = this.columnsMeta[0].field;
    }

    if (this.storeDataDto) {
      this.setUpGridState(this.storeDataDto);
    }
  }

  setAccountData(accountsData: any) {
    this.accountsDto = accountsData;
    this.isAccountExist = this.accountsDto.length > 0;
    if (this.tableMeta.isCustomerUser) {
      if (this.accounts.length === 1) {
        this.dropDownAccountSelected = this.accounts[0];
        this.accountCode = this.dropDownAccountSelected.code;
        setTimeout(() => { this.disableGrid = false; });
        this.sendAccountSelectedToParent(this.dropDownAccountSelected);
      }
      if (this.user.defaultAccountCode) {
        const defaultAccountIndex = this.accounts.findIndex((c: any) => c.code === this.user.defaultAccountCode);
        if (defaultAccountIndex > -1) {
          this.dropDownAccountSelected = this.accounts[defaultAccountIndex];
          this.accounts.splice(defaultAccountIndex, 1);
          this.accounts.unshift(this.dropDownAccountSelected);
          this.accountCode = this.dropDownAccountSelected.code;
          setTimeout(() => { this.disableGrid = false; });
          this.sendAccountSelectedToParent(this.dropDownAccountSelected);
        }
      }
      this.accounts.forEach((x: any) => {
        x.custom = this.customField(x);
      });
    }
    if (!this.isAccountExist) {
      this.gridData = [];
      setTimeout(() => { this.disableGrid = true; });
      this.totalRecords = 0;
      this.accountCode = '';
      this.filter.searchTerm = null;
      this.searchText = null;
    }
  }

  setUpContextMenu() {
    if (this.tableMeta.displayEditMenu) {
      this.contextMenuOptions.push({
        label: 'Edit',
        icon: 'ei ei-pencil',
        command: () =>
          this.onSelectingRow(this.gridTable.contextMenuSelection)
      });
    }
    if (this.tableMeta.displayDeleteMenu) {
      this.contextMenuOptions.push({
        label: 'Delete',
        icon: 'ei ei-trash2',
        command: () => this.deleteTableRow(this.gridTable.contextMenuSelection)
      });
    }
  }

  lazyLoadData(event: LazyLoadEvent) {
    if (event.filters.global && event.filters.global.value.length < 3) {
      return;
    }
    this.filter.searchTerm = '';
    // Setting the filter, sortColumn, sortingOrder, offset variables
    if (event.filters.global) {
      this.filter.searchTerm = event.filters.global.value;
    }
    if (event.sortField) {
      this.filter.sortingColumn = event.sortField;
    }
    if (event.sortOrder) {
      this.filter.sortingOrder = event.sortOrder > 0;
    }
    this.filter.pageSize = this.paginationState.rows;

    // PageNo reset to 1 if filter is applied
    if (this.previousSearch !== this.filter.searchTerm) {
      this.filter.pageNumber = 1;
      this.paginationState.first = 0;
    } else {
      this.filter.pageNumber = (this.paginationState.first / this.paginationState.rows) + 1;
    }

    this.previousSearch = this.filter.searchTerm;

    // Making table event and pagination state in sync
    event.first = this.paginationState.first;
    event.rows = this.paginationState.rows;

    this.sendGridStateToParent(event);
    this.sendFilterDataToParent();
  }

  paginate(event: any) {
    this.setPageSettings(event);
    this.sendFilterDataToParent();
  }

  onPageInput(event: any) {
    this.paginationState.first = (+event.target.value - 1) * this.paginationState.rows;
    this.filter.pageNumber = +event.target.value;
    this.sendFilterDataToParent();
  }

  setPageSettings(event: any) {
    // event.first = Index of the first record
    // event.rows = Number of rows to display in new page
    // event.page = Index of the new page
    // event.pageCount = Total number of pages
    this.filter.pageSize = event.rows;
    this.filter.pageNumber = event.first / event.rows + 1;

    // Updating the pagination variable on page change
    this.paginationState.first = event.first;
    this.paginationState.rows = event.rows;
  }

  onSelectAccount(event: Account) {
    this.flag = false;
    this.accountCode = event.code;
    this.searchText = '';
    this.sendAccountSelectedToParent(event);
    if (this.gridTable) {
      this.sendGridStateToParent(this.gridTable.filters);
    }
  }

  accountSelected() {
    this.flag = false;
    if (this.dropDownAccountSelected) {
      this.accountCode = this.dropDownAccountSelected.code;
      this.searchText = '';
      this.sendAccountSelectedToParent(this.dropDownAccountSelected);
    } else {
      this.resetProductCategoryTable();
    }
  }

  resetProductCategoryTable() {
    this.accountCode = '';
    this.searchText = '';
    this.isAccountExist = true;
    this.sendAccountSelectedToParent(null);
    if (this.gridTable) {
      this.sendGridStateToParent(this.gridTable.filters);
    }
  }

  customField(account: any) {
    return account.code + ' - ' + account.name + ' - ' + account.address.city;
  }

  onCheckBoxChange() {
    this.noOfSelectedTableRows = this.selectedTableRows.length;
  }

  resolveFieldData(data: any, field: string) {
    return ResolveFieldData.resolve(data, field);
  }

  setUpGridState(state: any) {
    if (state.account) {
      this.accountSearch = state.account;
      state = this.resetObject(state);
      state.account.isFromState = true;
      this.onSelectAccount(state.account);
    }
    if (state.event.filters) {
      this.searchText = state.event.filters.global ? state.event.filters.global.value : '';
      if (this.gridTable) {
        this.gridTable.filters = state.event.filters;
      }
      this.defaultSortColumn = state.event.sortField;
      this.sortOrder = state.event.sortOrder;
    }
  }

  checkValidation() {
    if (this.isDataLoaded) {
      if (this.flag) {
        if (this.tableMeta.showAccountSearch) {
          if (this.accountCode.length > 0 && (this.totalRecords < 1 || this.totalRecords === undefined)) {
            return true;
          }
        } else if (this.totalRecords < 1 || this.totalRecords === undefined) {
          return true;
        }
      }
    }
  }

  // Emits event to parent if Create Button is clicked
  onCreateButtonClick() {
    this.createButtonEvent.emit();
  }

  // Emits the filter object to the parent on any change to grid state
  sendFilterDataToParent() {
    this.filterChangeEvent.emit(this.filter);
  }

  // Emits the row Data to the parent when a row is clicked
  onSelectingRow(rowData: any) {
    this.rowSelectedEvent.emit(rowData);
  }

  // Emits the rows selected to be deleted to the parent
  onMultipleDelete() {
    this.rowDeletionEvent.emit(this.selectedTableRows);
  }

  // Emits the row selected to be deleted to the parent
  deleteTableRow(rowData: any) {
    this.rowDeletionEvent.emit([rowData]);
  }

  // Emits the search text based on which account search should happen
  fillAccounts() {
    this.accountSearchEvent.emit(this.accountSearch);
  }

  // Emits the account which has been selected from the drop down
  sendAccountSelectedToParent(account: any) {
    this.accountSelectEvent.emit(account);
  }

  // Emits the current grid state to the parent
  sendGridStateToParent(event: any) {
    this.gridState.emit(event);
  }

  // Emits the row data which has been edited
  changeDefaultValue(rowData: any) {
    this.defaultValueRowEditEvent.emit(rowData);
  }

  isValidIntegerValue(data: any) {
    if (data && data > this.INTMAXVALUE) {
      this.defaultValueRowEditEvent.emit(); // disable the Save button when the value is > max int value
      return false;
    }
    return true;
  }
  validateIntegerValueOnPaste(data: any) {
    if (data && !isNaN(data) && data > this.INTMAXVALUE) {
      this.defaultValueRowEditEvent.emit(); // disable the Save button when the value is > max int value
      return false;
    }
    return true;
  }

  // Emits event to parent if Upload Button is clicked
  uploadWindow() {
    this.uploadButtonEvent.emit();
  }

  // Emits event to parent if Download Button is clicked
  downloadWindow() {
    this.downloadButtonEvent.emit();
  }

  private resetObject(data: any) {
    let newObj: any = {};
    Object.keys(data).forEach(function (key) {
      newObj[key] = Object.assign({}, data[key]);
    });
    return newObj;
  }
}
